import { memo, useCallback, useMemo } from 'react';
import { useDeleteIntegrationYoutube } from '../../../api/delete-integration-youtube.api';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY } from '../../../api/get-list-integration-youtube.api';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

export type TDeleteIntegrationYoutubeModalType = 'DeleteIntegrationYoutubeModal';
export interface IDeleteIntegrationYoutubeModalProps {
  organizationChannelId: string;
}

const TOAST_MESSAGES_DELETE_INTEGRATION_YOUTUBE = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete YouTube channel!',
};

const DeleteIntegrationYoutubeModal = memo(({ organizationChannelId }: IDeleteIntegrationYoutubeModalProps) => {
  const channelModalRootActions = useChannelModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteIntegrationYoutubeModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteIntegrationYoutubeModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationYoutube = useDeleteIntegrationYoutube({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_INTEGRATION_YOUTUBE.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_YOUTUBE_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteIntegrationYoutubeModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_INTEGRATION_YOUTUBE.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (organizationChannelId) {
      mutationDeleteIntegrationYoutube.mutate({
        organizationChannelId: organizationChannelId || '',
      });
    }
  }, [organizationChannelId, mutationDeleteIntegrationYoutube]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteIntegrationYoutubeModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteIntegrationYoutubeModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationYoutube.isPending}
      title="Delete YouTube Channel"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteIntegrationYoutubeModal.displayName = 'DeleteIntegrationYoutubeModal';

export default DeleteIntegrationYoutubeModal;
